//
// 32-bit integer marker representation
//
// index = 24-bit
// format = 8-bit
//
//           index           | format
// 00000000 00000000 00000000 00000000
//
// Example:
// 00000000 00000000 00010110 00000011
//

const FORMAT_BOLD = 1;
const FORMAT_ITALIC = 2;
const FORMAT_UNDERLINE = 4;
const FORMAT_STRIKE = 8;

/**
 * Format a string with yaa-tags
 *
 * @param input The input string
 * @returns The formatted string
 */
export function formatString(input?: string): string {
	if (!input) return "";
	return applyMarkers(...extractMarkers(input));
}

/**
 * Sanitize the input string and build a list of packed tag markers
 *
 * @param input The input string
 * @returns Sanitized string and packed markers
 */
function extractMarkers(input: string): [string, number[]] {
	const markers: number[] = [];

	let out = input;
	let idx = 0;
	let len = 0;
	let fmt = 0;
	let tag = false;

	for (const char of input) {
		if (char === "{") {
			tag = true;
			len++;
			continue;
		}

		if (!tag) {
			idx++;
			continue;
		}

		len++;

		if (char === "}") {
			markers.push((idx << 8) | fmt);
			out = out.slice(0, idx) + out.slice(idx + len);
			tag = false;
			fmt = 0;
			len = 0;
			continue;
		}

		switch (char) {
			case "b":
				fmt |= FORMAT_BOLD;
				break;
			case "i":
				fmt |= FORMAT_ITALIC;
				break;
			case "u":
				fmt |= FORMAT_UNDERLINE;
				break;
			case "s":
				fmt |= FORMAT_STRIKE;
				break;
			default:
				tag = false;
				idx += len;
				fmt = 0;
				len = 0;
		}
	}

	return [out, markers];
}

/**
 * Apply markers to a string
 *
 * @param input The input string
 * @param markers The markers
 * @returns The formatted string
 */
function applyMarkers(input: string, markers: number[]): string {
	let output = input;
	let state = 0;
	let offset = 0;

	function appendFormat(fmt: number) {
		let tag: string;

		if (fmt & FORMAT_BOLD) {
			tag = "b";
		} else if (fmt & FORMAT_ITALIC) {
			tag = "i";
		} else if (fmt & FORMAT_UNDERLINE) {
			tag = "u";
		} else if (fmt & FORMAT_STRIKE) {
			tag = "s";
		} else {
			return;
		}

		if (state & fmt) {
			output += `</${tag}>`;
			offset += 4;
			state &= ~fmt;
		} else {
			output += `<${tag}>`;
			offset += 3;
			state |= fmt;
		}
	}

	for (let i = 0; i < markers.length; i++) {
		const marker = markers[i];
		const idx = marker >> 8;
		const fmt = marker & 0xff;
		const trail = output.slice(idx + offset);

		output = output.slice(0, idx + offset);

		if (fmt & FORMAT_STRIKE) appendFormat(FORMAT_STRIKE);
		if (fmt & FORMAT_UNDERLINE) appendFormat(FORMAT_UNDERLINE);
		if (fmt & FORMAT_ITALIC) appendFormat(FORMAT_ITALIC);
		if (fmt & FORMAT_BOLD) appendFormat(FORMAT_BOLD);

		output += trail;
	}

	if (state & FORMAT_BOLD) appendFormat(FORMAT_BOLD);
	if (state & FORMAT_ITALIC) appendFormat(FORMAT_ITALIC);
	if (state & FORMAT_UNDERLINE) appendFormat(FORMAT_UNDERLINE);
	if (state & FORMAT_STRIKE) appendFormat(FORMAT_STRIKE);

	return output;
}
